/**
 * BagLock (rules v2) locked bag slot: a slot-sized shutter with hatching and a padlock, drawn
 * over the normal slot image at the bag's right end. When the first ticket line completes the
 * shackle springs up, the padlock pops off and the shutter fades, leaving a plain empty slot.
 * LockKey (rules v2) uses the same padlock on a board tile (TilePadlock) and a small key badge
 * (KEY_BADGE) on every piece of the key item.
 * Code-drawn like the glue tape: the textures are baked once per game into canvases.
 */
import * as Phaser from 'phaser';
import { C, css } from '../game/theme.js';
import { deco, dur } from '../fx/motion.js';

const COVER = 'fx_lock_cover';
const BODY = 'fx_lock_body';
const SHACKLE = 'fx_lock_shackle';
/** lockKey: a teal disc with a golden key, worn by every piece of the key item while the locks are shut */
export const KEY_BADGE = 'fx_key_badge';
const S = 128;

function canvas(w: number, h: number): [HTMLCanvasElement, CanvasRenderingContext2D] {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  return [c, c.getContext('2d')!];
}

function rounded(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function bake(scene: Phaser.Scene): void {
  if (scene.textures.exists(COVER)) return;
  // shutter: dark kraft with diagonal hatching and an ink rim, clearly not a place for items
  {
    const [c, ctx] = canvas(S, S);
    rounded(ctx, 6, 6, S - 12, S - 12, 20);
    ctx.fillStyle = css(C.inkSoft);
    ctx.fill();
    ctx.save();
    ctx.clip();
    ctx.strokeStyle = 'rgba(255,248,236,0.16)';
    ctx.lineWidth = 10;
    for (let x = -S; x < 2 * S; x += 24) {
      ctx.beginPath();
      ctx.moveTo(x, S);
      ctx.lineTo(x + S, 0);
      ctx.stroke();
    }
    ctx.fillStyle = 'rgba(0,0,0,0.18)';
    ctx.fillRect(0, 6, S, 12);
    ctx.restore();
    ctx.strokeStyle = css(C.ink);
    ctx.lineWidth = 6;
    rounded(ctx, 6, 6, S - 12, S - 12, 20);
    ctx.stroke();
    scene.textures.addCanvas(COVER, c);
  }
  // padlock body: mustard block, ink outline, keyhole
  {
    const [c, ctx] = canvas(S, 104);
    rounded(ctx, 10, 16, S - 20, 80, 18);
    ctx.fillStyle = 'rgba(74,44,42,0.3)';
    ctx.fill();
    rounded(ctx, 10, 8, S - 20, 80, 18);
    ctx.fillStyle = css(C.mustard);
    ctx.fill();
    ctx.fillStyle = 'rgba(255,255,255,0.45)';
    ctx.fillRect(24, 18, S - 48, 8);
    ctx.fillStyle = css(C.mustardDark);
    ctx.fillRect(18, 68, S - 36, 10);
    ctx.strokeStyle = css(C.ink);
    ctx.lineWidth = 7;
    rounded(ctx, 10, 8, S - 20, 80, 18);
    ctx.stroke();
    ctx.fillStyle = css(C.ink);
    ctx.beginPath();
    ctx.arc(S / 2, 40, 10, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillRect(S / 2 - 5, 42, 10, 22);
    scene.textures.addCanvas(BODY, c);
  }
  // shackle: a steel arch (outlined), its legs drop into the body
  {
    const [c, ctx] = canvas(S, 96);
    const arch = (w: number, color: string) => {
      ctx.beginPath();
      ctx.moveTo(34, 96);
      ctx.lineTo(34, 50);
      ctx.arc(S / 2, 50, S / 2 - 34, Math.PI, 0);
      ctx.lineTo(S - 34, 96);
      ctx.strokeStyle = color;
      ctx.lineWidth = w;
      ctx.stroke();
    };
    ctx.lineCap = 'butt';
    arch(26, css(C.ink));
    arch(14, '#c9c2bb');
    ctx.globalAlpha = 0.7;
    ctx.beginPath();
    ctx.arc(S / 2, 50, S / 2 - 38, Math.PI * 1.1, Math.PI * 1.45);
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 4;
    ctx.stroke();
    scene.textures.addCanvas(SHACKLE, c);
  }
  // key badge: teal disc, ink rim, a golden key lying on the diagonal
  {
    const [c, ctx] = canvas(S, S);
    ctx.beginPath();
    ctx.arc(S / 2, S / 2 + 6, S / 2 - 8, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(74,44,42,0.3)';
    ctx.fill();
    ctx.beginPath();
    ctx.arc(S / 2, S / 2, S / 2 - 8, 0, Math.PI * 2);
    ctx.fillStyle = css(C.teal);
    ctx.fill();
    ctx.lineWidth = 7;
    ctx.strokeStyle = css(C.ink);
    ctx.stroke();
    ctx.save();
    ctx.translate(S / 2, S / 2);
    ctx.rotate(-Math.PI / 4);
    const key = (grow: number, color: string) => {
      ctx.fillStyle = color;
      // bow (ring), shaft, two bits
      ctx.beginPath();
      ctx.arc(-24, 0, 17 + grow, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillRect(-10 - grow, -6 - grow, 46 + 2 * grow, 12 + 2 * grow);
      ctx.fillRect(22 - grow, 4, 8 + 2 * grow, 14 + grow);
      ctx.fillRect(10 - grow, 4, 7 + 2 * grow, 10 + grow);
    };
    key(5, css(C.ink));
    key(0, css(C.mustard));
    ctx.beginPath();
    ctx.arc(-24, 0, 7, 0, Math.PI * 2);
    ctx.fillStyle = css(C.teal);
    ctx.fill();
    ctx.lineWidth = 4;
    ctx.strokeStyle = css(C.ink);
    ctx.stroke();
    ctx.fillStyle = 'rgba(255,255,255,0.6)';
    ctx.fillRect(-6, -4, 36, 3);
    ctx.restore();
    scene.textures.addCanvas(KEY_BADGE, c);
  }
}

/** Bake the lock and key textures (idempotent). */
export function bakeLockArt(scene: Phaser.Scene): void {
  bake(scene);
}

export class LockedSlot extends Phaser.GameObjects.Container {
  private readonly cover: Phaser.GameObjects.Image;
  private readonly lockBody: Phaser.GameObjects.Image;
  private readonly shackle: Phaser.GameObjects.Image;
  /** the padlock (body + shackle) moves as one */
  private readonly padlock: Phaser.GameObjects.Container;
  opening = false;

  constructor(scene: Phaser.Scene) {
    bake(scene);
    super(scene, 0, 0);
    this.cover = scene.add.image(0, 0, COVER);
    this.shackle = scene.add.image(0, -S * 0.2, SHACKLE);
    this.lockBody = scene.add.image(0, S * 0.16, BODY);
    this.padlock = scene.add.container(0, 0, [this.shackle, this.lockBody]);
    this.add([this.cover, this.padlock]);
    this.setDepth(3002);
    scene.add.existing(this);
  }

  /** Place over slot centre (x, y), `size` px square. */
  place(x: number, y: number, size: number, animateMs: number): void {
    const s = size / S;
    this.cover.setScale(s * 0.94);
    this.padlock.setScale(s * 0.52);
    if (!animateMs) return void this.setPosition(x, y);
    this.scene.tweens.add({ targets: this, x, y, duration: animateMs, ease: 'Back.easeOut' });
  }

  /** Attention wiggle (mechanic intro): decorative only. */
  wiggle(): void {
    const d = deco(90);
    if (!d || this.opening) return;
    this.scene.tweens.add({ targets: this.padlock, angle: { from: -12, to: 12 }, duration: d, yoyo: true, repeat: 2, onComplete: () => this.padlock.setAngle(0) });
  }

  /** The lock opens and the shutter lifts; `onPop` fires as the padlock comes off. */
  open(onPop: ((x: number, y: number) => void) | null): Promise<void> {
    this.opening = true;
    this.scene.tweens.killTweensOf([this, this.padlock]);
    return new Promise((resolve) => {
      const d = dur(300);
      const done = () => {
        this.destroy();
        resolve();
      };
      if (!d) {
        onPop?.(this.x, this.y);
        return done();
      }
      const spring = deco(130);
      // the shackle springs up and swings open on its left leg
      if (spring) this.scene.tweens.add({ targets: this.shackle, y: this.shackle.y - S * 0.2, angle: -28, duration: spring, ease: 'Back.easeOut' });
      this.scene.time.delayedCall(spring, () => {
        onPop?.(this.x, this.y - this.cover.displayHeight * 0.2);
        this.scene.tweens.add({
          targets: this.padlock,
          y: -this.cover.displayHeight * 0.7,
          scale: this.padlock.scale * 1.2,
          angle: deco(1) ? 20 : 0,
          alpha: 0,
          duration: d,
          ease: 'Cubic.easeOut',
        });
        this.scene.tweens.add({ targets: this.cover, alpha: 0, duration: d * 0.8, ease: 'Quad.easeIn', onComplete: done });
      });
    });
  }
}

/**
 * LockKey (rules v2): the padlock on a locked board tile. A child of the tile's view, so it moves,
 * sorts and dims with the tile (the tile tints its images while covered). It hangs over the
 * tile's lower right corner so the item stays readable. When the key pops the shackle springs
 * up and the padlock flies off.
 */
export class TilePadlock extends Phaser.GameObjects.Container {
  private readonly lockBody: Phaser.GameObjects.Image;
  private readonly shackle: Phaser.GameObjects.Image;
  opening = false;

  /** `tile`: the tile's size in device px. */
  constructor(scene: Phaser.Scene, tile: number) {
    bake(scene);
    super(scene, tile * 0.25, tile * 0.2);
    this.shackle = scene.add.image(0, -S * 0.2, SHACKLE);
    this.lockBody = scene.add.image(0, S * 0.16, BODY);
    this.add([this.shackle, this.lockBody]);
    this.setScale((tile * 0.42) / S);
  }

  /** The images to tint with the tile (covered dimming). */
  get parts(): Phaser.GameObjects.Image[] {
    return [this.lockBody, this.shackle];
  }

  /** "Still locked": a quick side-to-side rattle (gameplay feedback, shortened under reduced motion). */
  rattle(): void {
    const d = dur(70);
    if (!d || this.opening) return;
    this.scene.tweens.killTweensOf(this);
    this.setAngle(0);
    this.scene.tweens.add({ targets: this, angle: { from: -16, to: 16 }, duration: d, yoyo: true, repeat: 2, ease: 'Sine.easeInOut', onComplete: () => this.setAngle(0) });
  }

  /** Attention wiggle (after the mechanic intro): decorative only. */
  wiggle(): void {
    const d = deco(90);
    if (!d || this.opening) return;
    this.scene.tweens.add({ targets: this, angle: { from: -12, to: 12 }, duration: d, yoyo: true, repeat: 2, onComplete: () => this.setAngle(0) });
  }

  /** The key arrived: the shackle springs, the padlock flies off; `onPop` gets its world position. */
  open(onPop: ((x: number, y: number) => void) | null): Promise<void> {
    this.opening = true;
    this.scene.tweens.killTweensOf([this, this.shackle]);
    this.setAngle(0);
    const m = this.getWorldTransformMatrix();
    const wx = m.tx;
    const wy = m.ty;
    return new Promise((resolve) => {
      const d = dur(280);
      const done = () => {
        this.destroy();
        resolve();
      };
      if (!d) {
        onPop?.(wx, wy);
        return done();
      }
      const spring = deco(120);
      for (const p of this.parts) p.clearTint();
      if (spring) this.scene.tweens.add({ targets: this.shackle, y: this.shackle.y - S * 0.22, angle: -30, duration: spring, ease: 'Back.easeOut' });
      this.scene.time.delayedCall(spring, () => {
        onPop?.(wx, wy);
        this.scene.tweens.add({
          targets: this,
          y: this.y - S * this.scale * 0.9,
          scale: this.scale * 1.25,
          angle: deco(1) ? 22 : 0,
          alpha: 0,
          duration: d,
          ease: 'Cubic.easeOut',
          onComplete: done,
        });
      });
    });
  }
}
